import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { SafeHtmlPipe } from '../../../shared/pipe/safe-html.pipe';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailMessageApiService } from '../services/email-message-api.service';
import { EmailMessageDetailResponse } from '../models/email-message.model';

/**
 * Modal de detalle de un correo enviado. Consume GET /api/emails/{id}.
 * Reutilizado por la lista "Correos enviados" y por la pestaña "Correos"
 * del detalle de un contacto. Recibe el id por @Input y carga al abrir.
 */
@Component({
  selector: 'app-correo-detail-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, SafeHtmlPipe],
  templateUrl: './correo-detail-modal.component.html',
})
export class CorreoDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() emailId: number | null = null;
  @Output() close = new EventEmitter<void>();

  email: EmailMessageDetailResponse | null = null;
  loading = false;
  error = '';

  activeTab: 'info' | 'eventos' = 'info';

  constructor(
    private emailApi: EmailMessageApiService,
    private datetime: DatetimeService,
    private toast: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['emailId']) {
      if (this.isOpen && this.emailId != null) {
        this.cargarDetalle(this.emailId);
      } else if (!this.isOpen) {
        this.email = null;
        this.error = '';
        this.loading = false;
        this.activeTab = 'info';
      }
    }
  }

  private cargarDetalle(id: number): void {
    this.loading = true;
    this.error = '';
    this.email = null;

    this.emailApi.getEmailById(id).subscribe({
      next: (data) => {
        this.email = data;
        this.loading = false;
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al cargar el correo';
        this.error = msg;
        this.loading = false;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  onTabChange(tab: 'info' | 'eventos'): void {
    this.activeTab = tab;
  }

  onClose(): void {
    this.close.emit();
  }

  formatDate(iso?: string | null): string {
    return this.datetime.formatDateTime(iso);
  }

  // ── Destinatarios ──

  get recipientsTo(): string {
    if (!this.email?.recipients?.length) return '—';
    const tos = this.email.recipients.filter((r) => r.kind === 'TO');
    const list = tos.length ? tos : this.email.recipients;
    return list.map((r) => (r.fullName ? `${r.fullName} <${r.email}>` : r.email)).join(', ');
  }

  // ── Badge de estado ──

  statusColor(status: string): string {
    switch (status) {
      case 'SENT':
      case 'DELIVERED':
        return 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400';
      case 'QUEUED':
      case 'SENDING':
      case 'DEFERRED':
        return 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-orange-400';
      case 'FAILED':
      case 'BOUNCED':
        return 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-500';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80';
    }
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      QUEUED: 'En cola',
      SENDING: 'Enviando',
      SENT: 'Enviado',
      DELIVERED: 'Entregado',
      FAILED: 'Fallido',
      BOUNCED: 'Rebotado',
      DEFERRED: 'Diferido',
    };
    return map[status] || status;
  }

  // ── Eventos ──

  eventLabel(type: string): string {
    const map: Record<string, string> = {
      OPENED: 'Abierto',
      CLICKED: 'Clic',
      DELIVERED: 'Entregado',
      BOUNCED: 'Rebotado',
      FAILED: 'Fallido',
      DEFERRED: 'Diferido',
      SENT: 'Enviado',
      QUEUED: 'En cola',
      COMPLAINED: 'Marcado como spam',
    };
    return map[type] || type;
  }

  eventDotColor(type: string): string {
    switch (type) {
      case 'OPENED':
      case 'CLICKED':
        return 'bg-brand-500';
      case 'DELIVERED':
      case 'SENT':
        return 'bg-success-500';
      case 'FAILED':
      case 'BOUNCED':
      case 'COMPLAINED':
        return 'bg-error-500';
      case 'DEFERRED':
      case 'QUEUED':
        return 'bg-warning-500';
      default:
        return 'bg-gray-400';
    }
  }

  // Adjuntos: tamaño legible.
  formatSize(bytes: number): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
